import type { QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { createBrowserRouter } from 'react-router';
import type { ApiRequestObject } from '../flotiq-client/types/api-request';
import type {
  ContentObject,
  ContentTypeDefinition,
  WithOptionalInternalProps,
} from '../flotiq-client/types/api-response';
import type {
  FlotiqGlobals,
  FlotiqModalConfig,
  FlotiqPluginAddCallback,
  FlotiqPluginApiClient,
  FlotiqPluginCallback,
  FlotiqPluginInfo,
  FlotiqSchemaModalConfig,
} from './types';

import * as Sentry from '@sentry/react';
import { toast } from 'react-hot-toast';
import { Jodit } from 'jodit-pro/es2015/jodit.js';

import ElementFromPlugin from '../../components/ElementFromPlugin/ElementFromPlugin';

import { getModal } from '../../contexts/ModalContext';

import SchemaModalContent from '../../form/SchemaModalContent/SchemaModalContent';
import i18n from '../../i18n';
import { getEnv } from '../env';
import { FlotiqScopedApiClient } from './flotiq-scoped-api-client';
import { PluginEventHandler } from './plugin-event-handler';

declare global {
  interface Window {
    FlotiqPlugins: FlotiqPluginsRegistry;
    initFlotiqPlugins: {
      pluginInfo: FlotiqPluginInfo;
      callback: FlotiqPluginCallback;
    }[];
  }
}

export type CallbackRegister = Record<
  string,
  Record<string, FlotiqPluginCallback[]>
>;

export type RegisteredPluginHandlers = Record<string, PluginEventHandler>;

const callbackRegister: CallbackRegister = {
  'flotiq.grid::add': {},
  'flotiq.grid.cell::render': {},
  'flotiq.grid.filter::render': {},
  'flotiq.form.field::config': {},
  'flotiq.form.field::render': {},
  'flotiq.form.sidebar-panel::add': {},
  'flotiq.form::add': {},
  'flotiq.form::after-submit': {},
  'flotiq.plugins.manage::render': {},
  'flotiq.plugins.manage::form-schema': {},
  'flotiq.plugin.settings::changed': {},
  'flotiq.plugin::removed': {},
  'flotiq.media.crop::config': {},
};

const pluginSettingsRegister: Record<string, string | undefined> = {};

const errorsRegister: Record<string, string[]> = {};

const registeredPluginHandlers: RegisteredPluginHandlers = {};
const loadedPluginScripts: Record<string, string> = {};

const systemPluginsIds = JSON.parse(getEnv('VITE_SYSTEM_PLUGINS') || '[]').map(
  ({ id }: { id: string }) => id,
);

/**
 * Main class for handling Flotiq plugins. It is responsible for registering, running and managing plugins.
 * A global instance of this class is available as `window.FlotiqPlugins`.
 *
 * @memberof FlotiqPlugins.Core
 */
class FlotiqPluginsRegistry {
  /** Space ID where the plugins are operating */
  spaceId: string | undefined | null = null;
  /** Feature flags for the current space */
  spaceFeatureFlags: Record<string, boolean> = {};
  /** Router instance used for navigation within the app */
  router: null | ReturnType<typeof createBrowserRouter> | undefined = null;
  /** Query client instance for managing fetch caches */
  queryClient: undefined | QueryClient;

  private readonly openModal = async (
    config: Omit<FlotiqModalConfig, 'content'> & {
      content: ReactNode;
    },
  ) => {
    const { id, buttons, title, content, size, hideClose, className } = config;
    const contentElement = <ElementFromPlugin results={[content]} />;

    const modal = getModal();
    return modal({
      id,
      title,
      buttons,
      content: contentElement,
      size,
      hideClose,
      dialogAdditionalClasses: className,
    });
  };

  private readonly openSchemaModal = async (
    config: FlotiqSchemaModalConfig,
  ) => {
    return this.openModal({
      ...config,
      buttons: [],
      content: <SchemaModalContent key="form" form={config.form} />,
    });
  };

  enabled = () => getEnv('VITE_ENABLE_UI_PLUGINS') === 'true';

  private readonly getCurrentLanguage = () => i18n.language;

  getLoadedPlugins = () =>
    Object.fromEntries(
      Object.entries(registeredPluginHandlers).filter(
        ([id]) => !systemPluginsIds.includes(id),
      ),
    );

  getPluginSettings = (id: string) => pluginSettingsRegister[id];
  setPluginSettings = (id: string, newSettings: string) => {
    pluginSettingsRegister[id] = newSettings;
  };

  private runPluginCode(code: string, id = '', isLocalhost = false) {
    if (!FlotiqPlugins.enabled()) {
      console.warn('Plugins are disabled, skipping plugin code execution');
      return;
    }

    const functionName =
      'plugins_' + (isLocalhost ? '__local_' : '') + id.replaceAll(/\W+/g, '_');

    const pluginScript = `
      with (this) {
        ${code}
      }
      //# sourceURL=${functionName}.js`;

    /**
     * This `new Function` is considered safe-ish as it is executing external plugin code by design.
     */
    new Function(pluginScript).call(window);
  }

  private readonly getApiUrl = () => getEnv('VITE_FLOTIQ_API_URL');

  private readonly getFeatureFlag = (flagName: string): boolean => {
    return this.spaceFeatureFlags[flagName] ?? false;
  };

  private readonly closeModal = (id: string, result: unknown) =>
    getModal().close(id, result);

  private readonly navigate = (url: string) => {
    setTimeout(() => {
      if (this.router && this.router.state.navigation.state === 'idle') {
        this.router.navigate(url);
      }
    }, 0);
  };

  async loadPlugin(id: string, url: string, settings?: string) {
    if (!FlotiqPlugins.enabled()) {
      console.warn('Plugins are disabled, skipping plugin loading', id, url);
      return;
    }
    try {
      const cacheKey = id + url;

      if (!loadedPluginScripts[cacheKey]) {
        const scriptText = await fetch(url).then((r) => r.text());
        loadedPluginScripts[cacheKey] = scriptText;
      }

      const pluginUrl = new URL(url);
      const isLocalhost = pluginUrl.hostname === 'localhost';

      FlotiqPlugins.runPluginCode(
        loadedPluginScripts[cacheKey],
        id,
        isLocalhost,
      );
      pluginSettingsRegister[id] = settings;
    } catch (e) {
      errorsRegister[id] = [
        ...(errorsRegister[id] || []),
        'Error while loading plugin script',
      ];

      const marketplaceOrigin = new URL(
        getEnv('VITE_MARKETPLACE_API_URL') || '',
      ).origin;
      const pluginOrigin = new URL(url).origin;

      if (marketplaceOrigin == pluginOrigin) {
        Sentry.captureException(
          new Error(
            `Error while loading official plugin script from url: ${url}`,
          ),
        );
      }

      console.error('Error while loading plugin script', e);
    }
  }

  /**
   * Register new plugin. If plugin with given ID already exists, it will be unregistered first.
   */
  add(pluginInfo: FlotiqPluginInfo, callback: FlotiqPluginAddCallback) {
    if (!FlotiqPlugins.enabled()) {
      console.warn(
        'Plugins are disabled, skipping plugin registration',
        pluginInfo?.id,
      );
      return;
    }
    const { id, permissions } = pluginInfo;
    if (registeredPluginHandlers[id]) {
      registeredPluginHandlers[id].unregister();
    }
    // event handler for the plugin
    const eventHandler = new PluginEventHandler(
      pluginInfo,
      FlotiqPlugins,
      callbackRegister,
      registeredPluginHandlers,
    );

    registeredPluginHandlers[id] = eventHandler;

    const client: FlotiqPluginApiClient = new Proxy(
      new FlotiqScopedApiClient(permissions, this.queryClient),
      {
        get: (target, prop: string) => {
          if (prop === 'getMediaUrl') return target.getMediaUrl.bind(target);
          if (prop === 'getContentTypes')
            return target.getContentTypes.bind(target);
          return {
            get: (id: string) => target.getObject(prop, id),
            getVersions: (id: string) => target.getObjectVersions(prop, id),
            getVersion: (id: string, version: string) =>
              target.getObjectVersion(prop, id, version),
            list: (params: ApiRequestObject) =>
              target.listObjects(prop, params),
            post: (object: WithOptionalInternalProps<ContentObject>) =>
              target.postObject(prop, object),
            put: (
              id: string,
              object: WithOptionalInternalProps<ContentObject>,
            ) => target.putObject(prop, id, object),
            patch: (id: string, object: Partial<ContentObject>) =>
              target.patchObject(prop, id, object),
            delete: (id: string) => target.deleteObject(prop, id),
            getContentType: () => target.getContentType(prop),
            putContentType: (
              object: WithOptionalInternalProps<ContentTypeDefinition>,
            ) => target.putContentType(prop, object),
          };
        },
      },
    ) as unknown as FlotiqPluginApiClient;

    callback(eventHandler, client, {
      Jodit,
      toast,
      getLanguage: this.getCurrentLanguage,
      openModal: this.openModal,
      openSchemaModal: this.openSchemaModal,
      getPluginSettings: () => this.getPluginSettings(id),
      setPluginSettings: (settings) => this.setPluginSettings(id, settings),
      getApiUrl: () => this.getApiUrl(),
      closeModal: (id, result = null) => this.closeModal(id, result),
      getSpaceId: () => this.spaceId,
      navigate: (url) => this.navigate(url),
      getFeatureFlag: (flagName) => this.getFeatureFlag(flagName),
    } as FlotiqGlobals);

    if (!systemPluginsIds.includes(id))
      console.info('Plugin registered ⚡', pluginInfo);
  }

  /**
   * Unregister plugin event handlers by id.
   */
  unregister(
    /** The id of the plugin that should be unregistered from the plugin list */
    pluginId: string,
  ) {
    if (!pluginId) {
      throw Error('Plugin id was not provided');
    }

    if (registeredPluginHandlers[pluginId]) {
      registeredPluginHandlers[pluginId].unregister();
    }
  }

  /**
   * Unregister plugin event handlers by id.
   */
  unregisterAllPlugins() {
    Object.keys(registeredPluginHandlers).forEach((pluginId) => {
      this.unregister(pluginId);
    });
  }

  private getResults(
    eventCallbacks: [string, FlotiqPluginCallback[]][],
    event: string,
    ...params: Parameters<FlotiqPluginCallback>
  ) {
    return eventCallbacks.reduce((results, [pluginId, pluginCallbacks]) => {
      results.push(
        ...pluginCallbacks.map((c) => {
          try {
            return c(...params);
          } catch (e) {
            console.error(
              'Error while running plugin callback',
              pluginId,
              event,
              e,
            );
            errorsRegister[pluginId] = [
              ...(errorsRegister[pluginId] || []),
              `Error while running plugin callback ${event}.`,
            ];
            return null;
          }
        }),
      );
      return results;
    }, [] as any[]);
  }

  /**
   * Execute all handlers for an event. Event name and parameters must be paired correctly.
   * @returns Array of results returned by the executed handlers.
   */
  run(
    /** An event name to run. Only specified plugin handlers of this event will be executed. */
    event: string,
    /** Parameters to pass to the handlers. */
    ...params: Parameters<FlotiqPluginCallback>
  ) {
    if (!callbackRegister[event]) return [];
    return this.getResults(
      Object.entries(callbackRegister[event]),
      event,
      ...params,
    );
  }

  /**
   * Execute all handlers for an event for the given plugin. Event name and parameters must be paired correctly.
   * @returns Array of results returned by the executed handlers.
   */
  runScoped(
    /** An event name to run. Only specified plugin handlers of this event will be executed. */
    event: string,
    /** Plugin id to run the event for */
    pluginId: string,
    /** Parameters to pass to the handlers. */
    ...params: Parameters<FlotiqPluginCallback>
  ) {
    if (!callbackRegister[event] || !pluginId) return [];

    return this.getResults(
      Object.entries(callbackRegister[event]).filter(([id]) => id === pluginId),
      event,
      ...params,
    );
  }

  hasPluginOneOfEvents(pluginId: string, events?: string[]) {
    if (!events?.length || !pluginId) return false;

    for (const event of events) {
      const pluginEvent = Object.keys(callbackRegister[event] || {}).find(
        (id) => id === pluginId,
      );
      if (pluginEvent) return true;
    }

    return false;
  }

  init() {
    if (window.initFlotiqPlugins) {
      window.initFlotiqPlugins.forEach(({ pluginInfo, callback }) => {
        window.FlotiqPlugins.add(pluginInfo, callback);
      });
      window.initFlotiqPlugins.length = 0;
    }
  }

  getLoadedPluginsIds() {
    return Object.keys(registeredPluginHandlers).filter(
      (id) => !systemPluginsIds.includes(id),
    );
  }

  getPluginErrors(pluginId: string) {
    return Object.values(errorsRegister[pluginId] || {});
  }
}

const FlotiqPlugins = new FlotiqPluginsRegistry();
window.FlotiqPlugins = FlotiqPlugins;

export default FlotiqPlugins;
